import clsx from 'clsx'
import React, { FC, KeyboardEventHandler, memo, useCallback, useContext, useEffect, useState } from 'react'

import { Button } from '@Components/Button'
import { SharedFacetItemProps } from '@Components/Facet/Display/Items/ItemProps'
import { TextField } from '@Components/FormElements/UI/TextField'
import { MetadataContext } from '@Globals/Metadata'
import { getCurrencyUnit } from '@Globals/PriceHelpers'
import { useTranslation } from '@Globals/Translations'
import { MultiRangeInput } from '@Shared/Components/Inputs/RangeSlider/MultiRangeInput'

import styles from './Price.module.scss'

interface PriceItemProps extends SharedFacetItemProps {
  autoApplyFilter?: boolean
  closeHandler?: () => void
  from: number
  max: number
  min: number
  onChange?: (from: number, to: number) => void
  to: number
}

export const PriceItem: FC<PriceItemProps> = memo(({
  autoApplyFilter,
  closeHandler,
  from,
  id,
  max,
  min,
  onChange,
  to,
}) => {
  const { t } = useTranslation('common')
  const [minSpinner, setMinSpinner] = useState(false)
  const [sliderSpinner, setSliderSpinner] = useState(false)
  const [maxSpinner, setMaxSpinner] = useState(false)
  const { store } = useContext(MetadataContext)
  const fraction = 10 ** (store.config?.currencyFraction ?? 2)
  const [state, setState] = useState<{ from: number | 0, to: number }>(
    {
      from: Math.floor(from / fraction) || 0,
      to: Math.ceil(to / fraction),
    },
  )
  const onChangeFrom = useCallback((event) => {
    const value = event.target.value === '' ? 0 : parseInt(event.target.value, 10)
    if (!Number.isNaN(value)) {
      setState(prev => ({
        ...prev,
        from: value,
      }))
    }
  }, [setState])

  const onChangeTo = useCallback((event) => {
    const value = event.target.value === '' ? 0 : parseInt(event.target.value, 10)
    if (!Number.isNaN(value)) {
      setState(prev => ({
        ...prev,
        to: value,
      }))
    }
  }, [setState])

  const handleOnSubmit = useCallback((close = true) => {
    if (onChange) {
      onChange((state.from || 0) * fraction, state.to * fraction)
    }
    if (close && closeHandler) {
      closeHandler()
    }
  }, [closeHandler, fraction, onChange, state.from, state.to])

  let typingTimer
  const doneTypingInterval = 1500
  const doneTyping = () => {
    handleOnSubmit(false)
    setMinSpinner(false)
    setMaxSpinner(false)
    setSliderSpinner(false)
  }

  const keyDown = (setSpinner: (value: boolean) => void) => {
    if (!autoApplyFilter) {
      return
    }
    setSpinner(true)
    clearTimeout(typingTimer)
  }
  const keyUp = () => {
    if (!autoApplyFilter) {
      return
    }
    clearTimeout(typingTimer)
    typingTimer = setTimeout(() => doneTyping(), doneTypingInterval)
  }

  const handleKeyPress = useCallback<KeyboardEventHandler<HTMLInputElement>>((event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleOnSubmit()
    }
  }, [handleOnSubmit])

  // If this facet is reset from outside, we need to return the input fields to their former glory.
  useEffect(() => {
    if (from === 0 && to === max) {
      setState({
        from: 0,
        to: Math.ceil(max / fraction),
      })
    }
  }, [fraction, from, max, to])

  const currencyUnit = getCurrencyUnit(store.id)

  return (
    <div className={styles.rangedInputsRoot} id={id}>
      <MultiRangeInput
        disabled={sliderSpinner}
        from={state.from || 0}
        fromChanged={value => onChangeFrom({ target: { value: value.toString() } })}
        maxLimit={Math.ceil(max / fraction)}
        minLimit={0}
        onActive={() => clearTimeout(typingTimer)}
        onBlur={() => {
          setSliderSpinner(true)
          keyUp()
        }}
        to={state.to || (Math.ceil(max / fraction))}
        toChanged={value => onChangeTo({ target: { value: value.toString() } })}
      />

      <div className={styles.rangedInputsWrapper}>
        <div className={styles.currencyUnitContainer}>
          <TextField
            className={clsx('textField', styles.textField)}
            id={`${id}-1`}
            inputMode="numeric"
            labelActive
            max={max}
            min={0}
            onChange={onChangeFrom}
            onKeyDown={() => keyDown(setMinSpinner)}
            onKeyPress={handleKeyPress}
            onKeyUp={keyUp}
            placeholder="0"
            type="text"
            value={state.from}
          />
          {!!autoApplyFilter && minSpinner && <div className={styles.loadingSpinner} />}
          <span className={styles.currencyUnit}>{currencyUnit}</span>
        </div>
        {!!autoApplyFilter && sliderSpinner && <div className={styles.spinner} />}
        <div className={styles.currencyUnitContainer}>
          <TextField
            className={clsx('textField', styles.textField)}
            id={`${id}-2`}
            inputMode="numeric"
            labelActive
            max={max}
            min={min}
            onChange={onChangeTo}
            onKeyDown={() => keyDown(setMaxSpinner)}
            onKeyPress={handleKeyPress}
            onKeyUp={keyUp}
            type="text"
            value={state.to}
          />
          {!!autoApplyFilter && maxSpinner && <div className={styles.loadingSpinner} />}
          <span className={styles.currencyUnit}>{currencyUnit}</span>
        </div>
        {!autoApplyFilter && (
          <Button
            color="dark"
            onClick={() => handleOnSubmit(true)}
            style={{ padding: '5px 20px' }}
            type="submit"
            variant="outlined"
            width="auto"
          >
            {t('APPLY')}
          </Button>
        )}
      </div>
    </div>
  )
}, (prev, next) => prev.from === next.from && prev.to === next.to && prev.min === next.min && prev.max === next.max)

